import React from 'react';
import { CheckCircle2, XCircle, AlertTriangle, Award } from 'lucide-react';
import { SectionTitle, Reveal } from './Shared';
import { PACKAGES } from './PackagePicker';

const INCLUDED = [
  'Detailed review of your medical history and symptoms',
  'Review of previous blood tests, imaging and medical records',
  'Specialist rheumatology assessment and diagnostic opinion',
  'Personalised treatment and management recommendations',
  'Review of current medications and treatment options',
  'Advice on further investigations, if required',
  'Electronic consultation summary',
  'One follow-up consultation within 7 days, if included in your package',
];
const EXCLUDED = [
  'Emergency medical care',
  'Physical examination or MSK ultrasound',
  'Unlimited WhatsApp or telephone access',
  'Additional consultations beyond the agreed package',
  'Medical certificates, insurance documentation or medico-legal reports',
  'Prescription of medicines where cross-border prescribing is not legally permissible',
  'Guarantee of a definitive diagnosis without further evaluation',
];
const PROCESS = [
  'Book your appointment online.',
  'Complete the medical history form.',
  'Upload your medical reports at least 24 hours before consultation.',
  'Complete the international payment.',
  'Attend your scheduled video consultation.',
  'Receive your specialist advice and consultation summary.',
];

const List = ({ items, ok, testid }) => (
  <ul data-testid={testid} className="space-y-3 mt-5">
    {items.map((t) => (
      <li key={t} className="flex gap-3 text-sm text-slate-700 leading-relaxed">
        {ok ? <CheckCircle2 size={18} className="text-teal-600 shrink-0 mt-0.5" /> : <XCircle size={18} className="shrink-0 mt-0.5" style={{ color: 'var(--coral)' }} />}
        {t}
      </li>
    ))}
  </ul>
);

const DoctorBanner = () => (
  <div data-testid="intl-doctor-banner" className="rounded-3xl p-8 md:p-10 text-white flex flex-col md:flex-row md:items-center gap-6" style={{ background: 'linear-gradient(150deg,#0b3b3a,#0f766e)' }}>
    <span className="grid place-items-center w-14 h-14 rounded-2xl bg-white/10 border border-white/20 shrink-0"><Award size={26} className="text-amber-200" /></span>
    <div>
      <p className="text-xs uppercase tracking-[0.18em] text-amber-200 font-semibold">International patient online consultation</p>
      <h2 className="text-2xl md:text-3xl mt-2" style={{ fontWeight: 600 }}>Specialist Rheumatology &amp; Autoimmune Disease Care Across Borders</h2>
      <p className="mt-3 font-semibold">Dr Puneet Srivastava <span className="font-normal text-teal-100">· Senior Consultant Rheumatologist</span></p>
      <p className="text-sm text-teal-100 mt-1">MRCP (UK) | MRCP Rheumatology | EULAR | ECFMG (USA) · Consultant-level NHS experience in the United Kingdom.</p>
    </div>
  </div>
);

const Fees = () => (
  <div data-testid="intl-fees-table" className="bg-white rounded-3xl border border-teal-50 shadow-sm p-7">
    <h3 className="font-display text-xl text-teal-900" style={{ fontWeight: 600 }}>Consultation fees</h3>
    <div className="mt-4 divide-y divide-teal-50">
      {PACKAGES.map((p) => (
        <div key={p.id} data-testid={`intl-fee-${p.id}`} className="flex items-center justify-between gap-4 py-3 text-sm">
          <span className="text-slate-700">{p.name}</span><span className="font-semibold text-teal-900 whitespace-nowrap">{p.usd} USD</span>
        </div>
      ))}
    </div>
    <p className="text-xs text-slate-500 mt-3">Fees are subject to confirmation at the time of booking.</p>
  </div>
);

const Process = () => (
  <div className="bg-white rounded-3xl border border-teal-50 shadow-sm p-7">
    <h3 className="font-display text-xl text-teal-900" style={{ fontWeight: 600 }}>Consultation process</h3>
    <ol data-testid="intl-steps" className="mt-4 space-y-3">
      {PROCESS.map((t, i) => (
        <li key={t} className="flex gap-3 text-sm text-slate-700"><span className="grid place-items-center w-6 h-6 rounded-full bg-teal-600 text-white text-xs font-semibold shrink-0">{i + 1}</span>{t}</li>
      ))}
    </ol>
  </div>
);

export const IntlConsultDetails = () => (
  <section data-testid="intl-consult-details" className="mint-section py-20">
    <div className="container-x space-y-10">
      <Reveal><DoctorBanner /></Reveal>
      <p className="text-slate-600 leading-relaxed max-w-3xl">At Srigyan Rheumatology Clinic, we offer online specialist consultations for international patients seeking expert assessment and guidance for complex rheumatological and autoimmune conditions.</p>
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-3xl border border-teal-50 shadow-sm p-7"><SectionTitle eyebrow="Your consultation" title="What is included" /><List items={INCLUDED} ok testid="intl-included" /></div>
        <div className="bg-white rounded-3xl border border-teal-50 shadow-sm p-7"><SectionTitle eyebrow="Please note" title="What is not included" /><List items={EXCLUDED} testid="intl-excluded" /></div>
      </div>
      <div className="grid md:grid-cols-2 gap-6"><Process /><Fees /></div>
      <div data-testid="intl-important-info" className="rounded-3xl border border-amber-200 bg-amber-50 p-7">
        <h3 className="flex items-center gap-2 font-semibold text-amber-900"><AlertTriangle size={18} /> Important information</h3>
        <p className="text-sm text-amber-900/90 mt-2 leading-relaxed">Online consultation provides specialist medical advice based on the information and investigations shared by the patient. It may not replace a physical examination or in-person assessment. A local healthcare provider may be required for ongoing monitoring, prescriptions and emergency care.</p>
        <p className="text-sm font-semibold text-amber-900 mt-2">Online consultation is not suitable for medical emergencies.</p>
      </div>
      <div className="text-center">
        <p className="font-display text-xl text-teal-900" style={{ fontWeight: 600 }}>Srigyan Rheumatology Clinic</p>
        <p className="text-slate-600 text-sm mt-1">Expertise in Rheumatology. Care Beyond Borders. · <a href="https://www.rheumadoctor.com" className="text-teal-700 font-semibold">www.rheumadoctor.com</a></p>
      </div>
    </div>
  </section>
);
